import React, { useEffect, useState } from "react";
import { markAsRead } from "./function";

interface NotificationBoxProps {
    id: string;
    title: string;
    subtitle?: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    redirect_url?: string;
}

const ICONS: Record<string, string> = {
    reminder: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#059A7F" viewBox="0 0 256 256"><path d="M168,224a8,8,0,0,1-8,8H96a8,8,0,1,1,0-16h64A8,8,0,0,1,168,224Zm53.85-32A15.8,15.8,0,0,1,208,200H48a16,16,0,0,1-13.8-24.06C39.75,166.38,48,139.34,48,104a80,80,0,1,1,160,0c0,35.33,8.26,62.38,13.81,71.94A15.89,15.89,0,0,1,221.84,192ZM208,184c-7.73-13.27-16-43.95-16-80a64,64,0,1,0-128,0c0,36.06-8.28,66.74-16,80Z"></path></svg>',
    risk: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#E24B4A" viewBox="0 0 256 256"><path d="M144,200a16,16,0,1,1-16-16A16,16,0,0,1,144,200Zm-16-40a8,8,0,0,0,8-8V48a8,8,0,0,0-16,0V152A8,8,0,0,0,128,160Z"></path></svg>',
    system: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#96790F" viewBox="0 0 256 256"><path d="M208,40H48A16,16,0,0,0,32,56v56c0,52.72,25.52,84.67,46.93,102.19,23.06,18.86,46,25.26,47,25.53a8,8,0,0,0,4.2,0c1-.27,23.91-6.67,47-25.53C198.48,196.67,224,164.72,224,112V56A16,16,0,0,0,208,40Zm0,72c0,37.07-13.66,67.16-40.6,89.42A129.3,129.3,0,0,1,128,223.62a128.25,128.25,0,0,1-38.92-21.81C61.82,179.51,48,149.3,48,112l0-56,160,0ZM82.34,141.66a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32l-56,56a8,8,0,0,1-11.32,0Z"></path></svg>'
};

const NotificationBox: React.FC<NotificationBoxProps> = ({ id, title, subtitle, message, isRead, createdAt, redirect_url }) => {
    let textPart = message;
    let details: Record<string, any> | null = null;

    const [titleHandled, setTitleHandled] = useState<string>('');
    const [notificationType, setNotificationType] = useState<string>('system');

    const parseTitle = (rawTitle: string) => {
        let t2 = rawTitle;
        let type = 'system';

        if (rawTitle.startsWith("[true]") || rawTitle.toLowerCase().includes('reminder')) {
            type = 'reminder';
            t2 = rawTitle.replace("[true]", "").trim();
        } else if (rawTitle.startsWith("[WARNING]") || rawTitle.toLowerCase().includes('risk') || rawTitle.toLowerCase().includes('cảnh báo')) {
            type = 'risk';
            t2 = rawTitle.replace("[WARNING]", "").trim();
        }

        setNotificationType(type);
        setTitleHandled(t2);
    };

    useEffect(() => {
        parseTitle(title);
    }, [title]);

    // Format details nếu có dạng JSON trong message
    const match = message.match(/Details:\s*(\{.*\})/);
    if (match) {
        try {
            details = JSON.parse(match[1]);
            textPart = message.split("Details:")[0].trim();
        } catch (e) {
            details = null;
        }
    }

    const formatKey = (key: string) => {
        return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getTypeLabel = (type: string) => {
        switch(type) {
            case 'reminder': return 'Nhắc nhở';
            case 'risk': return 'Cảnh báo';
            default: return 'Hệ thống';
        }
    };

    return (
        <div
            onClick={async () => {
                await markAsRead(Number(id));
                if (redirect_url) {
                    window.open(redirect_url, '_self');
                } else {
                    window.open(`/notifications/${id}`, '_self');
                }
            }}
            className={`n-item ${!isRead ? 'unread' : ''}`}
            style={{ cursor: 'pointer', display: 'flex', gap: '11px', padding: '11px 8px', borderRadius: '12px', position: 'relative', transition: 'background .12s', marginBottom: '2px' }}
        >
            <div className={`n-rail type-${notificationType}`} style={{ width: '3px', borderRadius: '3px', alignSelf: 'stretch', flexShrink: 0, backgroundColor: notificationType === 'reminder' ? 'var(--teal, #00c9a7)' : notificationType === 'risk' ? 'var(--red, #e24b4a)' : 'var(--gold, #c9a227)' }}></div>
            
            <div className={`n-icon type-${notificationType}`} style={{ width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: notificationType === 'reminder' ? '#e6faf5' : notificationType === 'risk' ? '#fdecec' : '#faf3df' }}
                 dangerouslySetInnerHTML={{ __html: ICONS[notificationType] || ICONS.system }}
            />

            <div className="n-body" style={{ flex: 1, minWidth: 0 }}>
                <div className="n-top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span className="n-title" style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink, #0b1c2e)' }}>{titleHandled}</span>
                    <span className={`n-badge type-${notificationType}`} style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', flexShrink: 0, backgroundColor: notificationType === 'reminder' ? '#e6faf5' : notificationType === 'risk' ? '#fdecec' : '#faf3df', color: notificationType === 'reminder' ? '#059a7f' : notificationType === 'risk' ? '#e24b4a' : '#96790f' }}>
                        {getTypeLabel(notificationType)}
                    </span>
                </div>
                <div className="n-desc" style={{ fontSize: '12.5px', color: 'var(--ink-soft, #5b6472)', lineHeight: 1.45, marginTop: '2px', wordBreak: 'break-word' }}>
                    {textPart}
                </div>
                {details && (
                    <ul style={{ paddingLeft: '15px', marginTop: '4px', fontSize: '12px', color: 'var(--ink-soft, #5b6472)' }}>
                        {Object.entries(details).map(([key, value]) => (
                            <li key={key}>
                                {formatKey(key)}: {String(value ?? "N/A")}
                            </li>
                        ))}
                    </ul>
                )}
                <div className="n-time" style={{ fontSize: '11px', color: 'var(--ink-faint, #93989f)', marginTop: '5px' }}>
                    {new Date(createdAt).toLocaleString()}
                </div>
            </div>

            {!isRead && (
                <div className="n-unread-dot" style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#2b7de9', marginTop: '4px', flexShrink: '0' }}></div>
            )}
        </div>
    );
};

export default NotificationBox;